<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-explicit-any */
const emit = defineEmits<{
  (event: 'mouseenter'): void
  (event: 'mouseleave'): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'update:value', value: any | null): void
  (event: 'reset', value: any | null): void
}>()
const props = withDefaults(
  defineProps<{
    disabled?: boolean
    values?: any[]
    value?: any | null | undefined
    default?: any | null
    triggerEvent?: 'blur' | 'change' | 'input'
    suffix?: string | boolean
  }>(),
  {
    disabled: false,
    values: () => [],
    value: undefined,
    default: null,
    min: 0,
    max: 50,
    step: 5,
    triggerEvent: 'blur',
    suffix: false,
  }
)

const internalValue = ref(typeof props.value !== 'undefined' ? props.value : null)
const value = computed({
  get: () => (typeof props.value !== 'undefined' ? props.value : internalValue.value),
  set: (v: any | null) => emit('update:value', (internalValue.value = v)),
})

const update = (v: any | null) => (value.value = v)
const reset = (v?: any | null) => emit('reset', (value.value = v))
const onChange = (ev: Event) => {
  if (ev && ev.target) value.value = props.values[(ev.target as HTMLSelectElement).selectedIndex] || null
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const { class: hoverClass, events: hoverEvents } = useHover(emit, props)
const { class: focusClass, events: focusEvents } = useFocus(emit, props)

defineExpose({ update, reset })
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
  <select :aria-disabled="props.disabled"
    :class="['tw-select', props.disabled && 'is--disabled', hoverClass, focusClass]" :disabled="props.disabled"
    :tabindex="props.disabled ? -1 : 0" v-on="{ [props.triggerEvent]: onChange, ...hoverEvents, ...focusEvents }">
    <option v-for="(v, idx) in props.values" :key="`TwSelect_Option_${idx}`" :selected="value === v" :value="v">
      {{ v }} {{ props.suffix ? props.suffix : '' }}
    </option>
  </select>
</template>

<style lang="scss" scoped>
@import "~/assets/styles/tw-variables.scss";
@import "~/assets/styles/tw-mixins.scss";

$primary: #fa9400;
$white: #fff;
$black: #000;
$gray: #8f8f8f;
$tw-component--gap: 0.25em;

@mixin tw-color-set($color: null, $backgroundColor: null, $borderColor: null, $textDecorationColor: null) {
  color: $color;
  background-color: $backgroundColor;
  border-color: $borderColor;
  text-decoration-color: $textDecorationColor;
}

.tw-select {
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: $tw-component--gap;
  padding: $tw-component--gap;
  border: 1px solid transparent;
  @include tw-color-set($black, $white, $gray);
  font-size: 1em;
  font-family: 'Sofia W01', sans-serif;

  &.is--hovered {
    @include tw-color-set(null, null, $primary);
  }

  &.is--disabled {
    @include tw-color-set($white, $gray, $gray);
  }
}
</style>
